import { useState, useEffect } from 'react';
import { X, Users } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Agency {
  name: string;
  company: string;
}

interface AnnualObjective {
  fiscal_year: number;
  annual_objective: number;
}

interface Negotiator {
  id: string;
  firstname: string;
  lastname: string;
  role: string;
  status: string;
  archived: boolean;
  departure_date: string | null;
  arrival_date: string | null;
  
  has_custom_objectives: boolean;
  monthly_objectives: Record<string, Record<string, number>> | null;
  agencies: {
    name: string;
  }[];
  annual_objectives?: AnnualObjective[];
}

interface NegotiatorModalProps {
  negotiator: Negotiator | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const MONTHS = [
  { value: '04', label: 'Avril' },
  { value: '05', label: 'Mai' },
  { value: '06', label: 'Juin' },
  { value: '07', label: 'Juillet' },
  { value: '08', label: 'Août' },
  { value: '09', label: 'Septembre' },
  { value: '10', label: 'Octobre' },
  { value: '11', label: 'Novembre' },
  { value: '12', label: 'Décembre' },
  { value: '01', label: 'Janvier' },
  { value: '02', label: 'Février' },
  { value: '03', label: 'Mars' }
];

export default function NegotiatorModal({ negotiator, isOpen, onClose, onSuccess }: NegotiatorModalProps) {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState<number>(() => {
    const today = new Date();
    const month = today.getMonth() + 1;
    return month <= 4 ? today.getFullYear() - 1 : today.getFullYear();
  });

  // Générer la liste des années fiscales de 2023-2024 à 2030-2031
  const fiscalYears = Array.from({ length: 8 }, (_, i) => 2023 + i);

  const [formData, setFormData] = useState({
    agencies: [{ name: '', start_date: '', end_date: '', objective_percentage: 100 }],
    firstname: '',
    lastname: '',
    role: 'négociateur',
    status: 'conseiller',
    archived: false,
    arrival_date: new Date().toISOString().split('T')[0], // Default to today
    departure_date: '',
    has_custom_objectives: {} as Record<number, boolean>,
    monthly_objectives: {} as Record<string, Record<string, number>>,
    annual_objectives: {} as Record<number, number>
  });

  useEffect(() => {
    loadAgencies();
  }, []);

  useEffect(() => {
    if (negotiator) {
      loadNegotiatorObjectives();
    }
  }, [negotiator]);

  const loadAgencies = async () => {
    try {
      const { data, error } = await supabase
        .from('agencies')
        .select('name, company')
        .eq('active', true)
        .order('company')
        .order('name');

      if (error) throw error;
      setAgencies(data || []);
    } catch (err) {
      console.error('Error loading agencies:', err);
    }
  };

  const loadNegotiatorObjectives = async () => {
    if (!negotiator?.id) return;

    try {
      console.log('Loading objectives for negotiator:', negotiator.id);
      
      // Load negotiator agencies with dates and percentages
      const { data: agencyData, error: agencyError } = await supabase
        .from('negotiator_agencies')
        .select('agency, start_date, end_date, objective_percentage')
        .eq('negotiator_id', negotiator.id);

      if (agencyError) throw agencyError;

      console.log('Loaded agency data:', agencyData);
      console.log('Agency data JSON:', JSON.stringify(agencyData, null, 2));

      // Load annual objectives for all fiscal years
      const { data: annualObjectives, error: objectivesError } = await supabase
        .from('negotiator_annual_objectives')
        .select('fiscal_year, annual_objective')
        .eq('negotiator_id', negotiator.id);

      if (objectivesError) throw objectivesError;

      // Convert to Record format
      const annualObjectivesMap = (annualObjectives || []).reduce((acc, obj) => {
        acc[obj.fiscal_year] = obj.annual_objective;
        return acc;
      }, {} as Record<number, number>);

      // Initialize monthly objectives with all months set to 0
      const initialMonthlyObjectives = negotiator.monthly_objectives || {};
      const hasCustomObjectives = {} as Record<number, boolean>;

      fiscalYears.forEach(year => {
        // Check if this year has custom objectives
        hasCustomObjectives[year] = Boolean(initialMonthlyObjectives[year]);

        if (!initialMonthlyObjectives[year]) {
          initialMonthlyObjectives[year] = {};
        }
        MONTHS.forEach(month => {
          if (typeof initialMonthlyObjectives[year][month.value] !== 'number') {
            initialMonthlyObjectives[year][month.value] = 0;
          }
        });
      });

      // Initialize form data with negotiator info and objectives
      // Si agencyData existe, l'utiliser, sinon utiliser les données du negotiator
      const agenciesToUse = agencyData && agencyData.length > 0 
        ? agencyData.map(agency => {
            const percentage = typeof agency.objective_percentage === 'number' 
              ? agency.objective_percentage 
              : 100;
            console.log(`Loading agency ${agency.agency}: objective_percentage = ${agency.objective_percentage} (type: ${typeof agency.objective_percentage}), using: ${percentage}`);
            return {
              name: agency.agency,
              start_date: agency.start_date || new Date().toISOString().split('T')[0],
              end_date: agency.end_date || '',
              objective_percentage: percentage
            };
          })
        : (negotiator.agencies || []).map(agency => ({
            name: agency.name,
            start_date: new Date().toISOString().split('T')[0],
            end_date: '',
            objective_percentage: 100
          }));

      console.log('Agencies to use in form:', agenciesToUse);
      console.log('Agencies JSON:', JSON.stringify(agenciesToUse, null, 2));

      setFormData({
        agencies: agenciesToUse.length > 0 
          ? agenciesToUse 
          : [{ name: '', start_date: new Date().toISOString().split('T')[0], end_date: '', objective_percentage: 100 }],
        firstname: negotiator.firstname,
        lastname: negotiator.lastname,
        role: negotiator.role || 'négociateur',
        status: negotiator.status || 'conseiller',
        archived: negotiator.archived || false,
        arrival_date: negotiator.arrival_date || '',
        departure_date: negotiator.departure_date || '',
        has_custom_objectives: hasCustomObjectives,
        monthly_objectives: initialMonthlyObjectives,
        annual_objectives: annualObjectivesMap
      });
    } catch (err: any) {
      console.error('Error loading negotiator objectives:', err);
      setError(err.message);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!formData.firstname.trim() || !formData.lastname.trim()) {
        throw new Error('Le prénom et le nom sont requis');
      }

      // Valider qu'il n'y a pas de doublons d'agence
      const agencyNames = formData.agencies.filter(a => a.name).map(a => a.name);
      const uniqueAgencyNames = new Set(agencyNames);
      if (uniqueAgencyNames.size !== agencyNames.length) {
        const duplicates = agencyNames.filter((name, i) => agencyNames.indexOf(name) !== i);
        throw new Error(`Agence en doublon : "${[...new Set(duplicates)].join(', ')}". Chaque agence ne peut apparaître qu'une seule fois.`);
      }

      // Valider que le total des pourcentages fait 100%
      const totalPercentage = formData.agencies
        .filter(a => a.name)
        .reduce((sum, a) => sum + (typeof a.objective_percentage === 'number' ? a.objective_percentage : 0), 0);

      if (Math.abs(totalPercentage - 100) > 0.01) {
        throw new Error(`Le total des pourcentages doit être égal à 100% (actuellement ${totalPercentage.toFixed(1)}%)`);
      }

      // Prepare monthly objectives data
      const monthlyObjectives = {} as Record<string, Record<string, number>>;
      
      // Only include years that have custom objectives enabled
      Object.entries(formData.has_custom_objectives).forEach(([year, hasCustom]) => {
        if (hasCustom) {
          monthlyObjectives[year] = formData.monthly_objectives[year];
        }
      });

      const negotiatorData = {
        firstname: formData.firstname.trim(),
        lastname: formData.lastname.trim(),
        role: formData.role,
        status: formData.status,
        archived: formData.archived,
        arrival_date: formData.arrival_date || null,
        departure_date: formData.departure_date || null,
        has_custom_objectives: Object.values(formData.has_custom_objectives).some(v => v),
        monthly_objectives: Object.keys(monthlyObjectives).length > 0 ? monthlyObjectives : null
      };

      let negotiatorId: string;

      if (negotiator) {
        // Update existing negotiator
        const { error: updateError } = await supabase
          .from('negotiators')
          .update(negotiatorData)
          .eq('id', negotiator.id);

        if (updateError) throw updateError;
        negotiatorId = negotiator.id;
      } else {
        // Create new negotiator
        const { data: newNegotiator, error: createError } = await supabase
          .from('negotiators')
          .insert([negotiatorData])
          .select()
          .single();

        if (createError) throw createError;
        if (!newNegotiator) throw new Error('Failed to create negotiator');
        negotiatorId = newNegotiator.id;
      }

      // Update agencies with percentages
      await supabase
        .from('negotiator_agencies')
        .delete()
        .eq('negotiator_id', negotiatorId);

      const agenciesToSave = formData.agencies
        .filter(a => a.name)
        .map(a => {
          const percentage = typeof a.objective_percentage === 'number' 
            ? a.objective_percentage 
            : 100;
          console.log(`Saving agency ${a.name}: objective_percentage = ${a.objective_percentage} (type: ${typeof a.objective_percentage}), saving: ${percentage}`);
          return {
            negotiator_id: negotiatorId,
            agency: a.name,
            start_date: a.start_date || new Date().toISOString().split('T')[0],
            end_date: a.end_date || null,
            objective_percentage: percentage
          };
        });

      console.log('Agencies to save:', JSON.stringify(agenciesToSave, null, 2));

      const { error: insertError } = await supabase
        .from('negotiator_agencies')
        .insert(agenciesToSave);

      if (insertError) {
        console.error('Error inserting agencies:', insertError);
        throw insertError;
      }

      // Update annual objectives for each fiscal year
      for (const [year, objective] of Object.entries(formData.annual_objectives)) {
        const fiscalYear = parseInt(year);
        if (isNaN(fiscalYear)) continue;

        // Only save annual objective if custom objectives are not enabled for this year
        if (!formData.has_custom_objectives[fiscalYear]) {
          await supabase
            .from('negotiator_annual_objectives')
            .upsert({
              negotiator_id: negotiatorId,
              fiscal_year: fiscalYear,
              annual_objective: objective
            }, {
              onConflict: 'negotiator_id,fiscal_year'
            });
        }
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error in handleSubmit:', err);
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleMonthlyObjectiveChange = (month: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      monthly_objectives: {
        ...prev.monthly_objectives,
        [selectedFiscalYear]: {
          ...prev.monthly_objectives[selectedFiscalYear],
          [month]: parseFloat(value) || 0
        }
      }
    }));
  };

  if (!isOpen) return null;

  // Ensure all months exist with at least 0 values for the selected year
  if (formData.has_custom_objectives[selectedFiscalYear] && !formData.monthly_objectives[selectedFiscalYear]) {
    const initialMonthlyValues = MONTHS.reduce((acc, month) => {
      acc[month.value] = 0;
      return acc;
    }, {} as Record<string, number>);

    setFormData(prev => ({
      ...prev,
      monthly_objectives: {
        ...prev.monthly_objectives,
        [selectedFiscalYear]: initialMonthlyValues
      }
    }));
  }

  // Calculer les totaux pour l'année sélectionnée
  const yearTotal = formData.has_custom_objectives[selectedFiscalYear]
    ? Object.values(formData.monthly_objectives[selectedFiscalYear] || {})
        .reduce((sum, value) => sum + (parseFloat(value.toString()) || 0), 0)
    : formData.annual_objectives[selectedFiscalYear] || 0;

  // Calculer le total des pourcentages
  const totalPercentage = formData.agencies
    .filter(a => a.name)
    .reduce((sum, a) => sum + (a.objective_percentage ?? 0), 0);
  const isPercentageValid = Math.abs(totalPercentage - 100) < 0.01;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl relative max-h-[92vh] flex flex-col shadow-2xl">

        {/* Header */}
        <div className="relative rounded-t-2xl overflow-hidden bg-gradient-to-r from-slate-800 via-slate-900 to-indigo-950 px-6 py-5 shrink-0">
          <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-500/10 rounded-full -translate-y-1/2 translate-x-1/3 pointer-events-none" />
          <div className="relative flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center shrink-0">
                <Users size={18} className="text-white/80" />
              </div>
              <h2 className="text-lg font-bold text-white tracking-tight">
                {negotiator ? 'Modifier le négociateur' : 'Nouveau négociateur'}
              </h2>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors shrink-0">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-6">
          <form id="negotiator-form" onSubmit={handleSubmit} className="space-y-5">

            {/* Informations personnelles */}
            <div className="bg-slate-50 rounded-xl border border-slate-100 p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Informations personnelles</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Prénom</label>
                  <input
                    type="text"
                    value={formData.firstname}
                    onChange={(e) => setFormData(prev => ({ ...prev, firstname: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Nom</label>
                  <input
                    type="text"
                    value={formData.lastname}
                    onChange={(e) => setFormData(prev => ({ ...prev, lastname: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Fonction et statut */}
            <div className="bg-slate-50 rounded-xl border border-slate-100 p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Fonction et statut</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Fonction</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                    required
                  >
                    <option value="directeur">Directeur</option>
                    <option value="responsable">Responsable</option>
                    <option value="négociateur">Négociateur</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Statut</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                    required
                  >
                    <option value="directeur">Directeur</option>
                    <option value="conseiller">Conseiller</option>
                    <option value="confirmé">Confirmé</option>
                    <option value="senior">Senior</option>
                    <option value="expert">Expert</option>
                    <option value="animateur">Animateur</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Objectifs */}
            <div className="bg-slate-50 rounded-xl border border-slate-100 p-4 space-y-3">
              <h3 className="text-sm font-semibold text-slate-700">Objectifs</h3>
              <div className="flex items-center justify-between">
                <select
                  value={selectedFiscalYear}
                  onChange={(e) => setSelectedFiscalYear(parseInt(e.target.value))}
                  className="px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                >
                  {fiscalYears.map(year => (
                    <option key={year} value={year}>Année {year} - {year + 1}</option>
                  ))}
                </select>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    id="customObjectives"
                    checked={formData.has_custom_objectives[selectedFiscalYear] || false}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      has_custom_objectives: { ...prev.has_custom_objectives, [selectedFiscalYear]: e.target.checked }
                    }))}
                    className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm font-medium text-slate-700">Objectifs mensuels personnalisés</span>
                </label>
              </div>

              {formData.has_custom_objectives[selectedFiscalYear] ? (
                <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
                  <h4 className="text-sm font-semibold text-slate-700">
                    Objectifs mensuels {selectedFiscalYear} - {selectedFiscalYear + 1}
                  </h4>
                  <div className="grid grid-cols-3 gap-3">
                    {MONTHS.map((month) => (
                      <div key={month.value}>
                        <label className="block text-xs font-medium text-slate-500 mb-1">
                          {month.label} {parseInt(month.value) <= 3 ? selectedFiscalYear + 1 : selectedFiscalYear}
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={formData.monthly_objectives[selectedFiscalYear]?.[month.value] || 0}
                          onChange={(e) => handleMonthlyObjectiveChange(month.value, e.target.value)}
                          className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between items-center px-4 py-3 bg-indigo-50 rounded-xl border border-indigo-100">
                    <span className="text-sm font-medium text-indigo-700">Total annuel</span>
                    <span className="text-base font-bold text-indigo-700">
                      {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(yearTotal)}
                    </span>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Objectif annuel (€)</label>
                  <input
                    type="number"
                    value={formData.annual_objectives[selectedFiscalYear] || ''}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      annual_objectives: { ...prev.annual_objectives, [selectedFiscalYear]: parseFloat(e.target.value) || 0 }
                    }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                    min="0"
                    step="1"
                  />
                </div>
              )}
            </div>

            {/* Agences */}
            <div className="bg-slate-50 rounded-xl border border-slate-100 p-4 space-y-3">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-semibold text-slate-700">Agences</h3>
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-medium ${isPercentageValid ? 'text-emerald-600' : 'text-red-600'}`}>
                    Total: {totalPercentage.toFixed(1)}% {isPercentageValid ? '✓' : '(doit être 100%)'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({
                      ...prev,
                      agencies: [...prev.agencies, { name: '', start_date: new Date().toISOString().split('T')[0], end_date: '', objective_percentage: 0 }]
                    }))}
                    className="p-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-600 transition-colors text-sm font-medium px-3"
                  >
                    + Ajouter une agence
                  </button>
                </div>
              </div>

              <div className="space-y-3">
                {formData.agencies.map((agency, index) => (
                  <div key={index} className="bg-white rounded-xl border border-slate-200 p-3">
                    <div className="grid grid-cols-5 gap-3 items-end">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Agence</label>
                        <select
                          value={agency.name}
                          onChange={(e) => {
                            const newAgencies = [...formData.agencies];
                            newAgencies[index] = { ...newAgencies[index], name: e.target.value };
                            setFormData(prev => ({ ...prev, agencies: newAgencies }));
                          }}
                          className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                          required
                        >
                          <option value="">Sélectionner une agence</option>
                          {agencies.map((a) => (
                            <option key={a.name} value={a.name}>{a.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Date de début</label>
                        <input
                          type="date"
                          value={agency.start_date || ''}
                          onChange={(e) => {
                            const newAgencies = [...formData.agencies];
                            newAgencies[index] = { ...newAgencies[index], start_date: e.target.value };
                            setFormData(prev => ({ ...prev, agencies: newAgencies }));
                          }}
                          className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                          required
                          disabled={loading}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Date de fin</label>
                        <input
                          type="date"
                          value={agency.end_date || ''}
                          onChange={(e) => {
                            const newAgencies = [...formData.agencies];
                            newAgencies[index] = { ...newAgencies[index], end_date: e.target.value || '' };
                            setFormData(prev => ({ ...prev, agencies: newAgencies }));
                          }}
                          className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                          disabled={loading}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">% Objectif</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={agency.objective_percentage ?? 0}
                          onChange={(e) => {
                            const newAgencies = [...formData.agencies];
                            const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                            newAgencies[index] = { ...newAgencies[index], objective_percentage: isNaN(value) ? 0 : value };
                            setFormData(prev => ({ ...prev, agencies: newAgencies }));
                          }}
                          className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                          required
                          disabled={loading}
                        />
                      </div>
                      {formData.agencies.length > 1 && (
                        <div className="flex justify-center">
                          <button
                            type="button"
                            onClick={() => {
                              const newAgencies = formData.agencies.filter((_, i) => i !== index);
                              setFormData(prev => ({ ...prev, agencies: newAgencies }));
                            }}
                            className="p-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 transition-colors"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Dates importantes */}
            <div className="bg-slate-50 rounded-xl border border-slate-100 p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Dates importantes</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date d'arrivée</label>
                  <input
                    type="date"
                    value={formData.arrival_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, arrival_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date de départ</label>
                  <input
                    type="date"
                    value={formData.departure_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, departure_date: e.target.value }))}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/40 focus:border-indigo-400 transition-colors"
                  />
                  <p className="mt-1 text-xs text-slate-500">Après cette date, le CA sera attribué à la Direction</p>
                </div>
              </div>
            </div>

            {/* Options avancées */}
            <div className="bg-slate-50 rounded-xl border border-slate-100 p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Options avancées</h3>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.archived}
                  onChange={(e) => setFormData(prev => ({ ...prev, archived: e.target.checked }))}
                  className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  id="archived"
                />
                <span className="text-sm font-medium text-slate-700">Archiver le négociateur</span>
              </label>
              <p className="mt-1.5 text-xs text-slate-500">
                Un négociateur archivé n'apparaîtra plus dans les listes actives mais restera visible dans l'historique.
              </p>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-100 bg-slate-50/60 px-6 py-4 flex justify-end gap-3 shrink-0 rounded-b-2xl">
          {error && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-red-700 text-sm flex-1">
              {error}
            </div>
          )}
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-slate-700 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors"
          >
            Annuler
          </button>
          <button
            form="negotiator-form"
            type="submit"
            disabled={loading}
            className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Chargement…' : (negotiator ? 'Enregistrer' : 'Créer')}
          </button>
        </div>
      </div>
    </div>
  );
}